import { useQuery } from "@tanstack/react-query";
import apiV2 from "api-v2";
import { StaleTime } from "newui/store/tanstack-base";
import { unwrapApiV2 } from "../utils/unwrapApiV2";
import type { RfxReadOnlyPreviewResource } from "../types/rfx-read-only-preview.type";

/**
 * `GET /api/v2/rfq/{rfxUuid}/preview` — addressed by RFx UUID only (Task 003/004's
 * `{rfxOr403}` binder resolves by uuid; the global `{RFx}` numeric-id binding is
 * NOT used on this route). Passing a numeric id here 403s.
 */
/**
 * Exported (not just wrapped in the hook below) so non-react-query callers —
 * e.g. `RfxDataProvider`'s panel-member fallback (TASK-028) — can call it
 * imperatively inside an effect.
 */
export const getRfxReadOnlyPreview = async (
	rfxUuid: string,
	suppressToastOn403 = false
): Promise<RfxReadOnlyPreviewResource> => {
	const requestArgs: [string, Record<string, unknown>?] = suppressToastOn403
		? [`rfq/${rfxUuid}/preview`, { expectedErrors: [403] }]
		: [`rfq/${rfxUuid}/preview`];
	const { data } = await apiV2.get(...requestArgs);
	return unwrapApiV2<RfxReadOnlyPreviewResource>(data);
};

export const rfxReadOnlyPreviewQueryKey = (rfxUuid: string | undefined) =>
	["rfx-read-only-preview", rfxUuid ?? null] as const;

export const useRfxReadOnlyPreview = (rfxUuid: string | undefined) =>
	useQuery<RfxReadOnlyPreviewResource>({
		queryKey: rfxReadOnlyPreviewQueryKey(rfxUuid),
		queryFn: () => getRfxReadOnlyPreview(rfxUuid as string),
		enabled: !!rfxUuid,
		staleTime: StaleTime.Short,
		retry: false,
	});
